import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeviceList } from './DeviceList';
import { LocationTree } from './LocationTree';
import { SummaryBar } from './SummaryBar';
import {
  computeStats,
  descendantLeafDeviceIds,
  filterDevicesBySearch,
} from './selectors';
import {
  DeviceItem,
  DeviceSelectorProps,
  LocationNode,
  NodeId,
  ScopeStats,
} from './types';

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  data,
  initialSelectedIds,
  onSelectionChange,
  className,
  renderDeviceMeta,
}) => {
  const { nodes, devices, rootIds } = data;

  // Selection state — controlled output, but seeded from initialSelectedIds
  const [selectedIds, setSelectedIds] = useState<Set<NodeId>>(
    () => new Set(initialSelectedIds ?? [])
  );

  // Re-seed when initialSelectedIds reference changes (e.g. after server load)
  const lastSeedRef = useRef(initialSelectedIds);
  useEffect(() => {
    if (lastSeedRef.current !== initialSelectedIds) {
      lastSeedRef.current = initialSelectedIds;
      setSelectedIds(new Set(initialSelectedIds ?? []));
    }
  }, [initialSelectedIds]);

  // Notify parent of selection changes
  const lastNotifiedRef = useRef<NodeId[] | null>(null);
  useEffect(() => {
    const arr = Array.from(selectedIds).sort();
    const prev = lastNotifiedRef.current;
    if (prev && prev.length === arr.length && prev.every((v, i) => v === arr[i])) return;
    lastNotifiedRef.current = arr;
    onSelectionChange(arr);
  }, [selectedIds, onSelectionChange]);

  // Navigation state
  const [focusedNodeId, setFocusedNodeId] = useState<NodeId | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const nodesById = useMemo(() => {
    const m = new Map<NodeId, LocationNode>();
    nodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [nodes]);

  const labNameById = useMemo(() => {
    const m = new Map<NodeId, string>();
    nodes.filter((n) => n.type === 'lab').forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nodes]);

  const focusedNodeName = useMemo(() => {
    if (focusedNodeId === null) return 'All locations';
    return nodesById.get(focusedNodeId)?.name ?? '—';
  }, [focusedNodeId, nodesById]);

  // Devices in the focused scope (recursive), then search-filtered
  const visibleDevices = useMemo(() => {
    const inScopeIds = new Set(descendantLeafDeviceIds(focusedNodeId, nodes, devices));
    const inScope = devices.filter((d) => inScopeIds.has(d.id));
    return filterDevicesBySearch(inScope, searchQuery);
  }, [focusedNodeId, nodes, devices, searchQuery]);

  // Stats helpers — recompute per render is OK for v1 (≤ 500 devices)
  const computeNodeStats = useCallback(
    (nodeId: NodeId): ScopeStats => {
      const ids = descendantLeafDeviceIds(nodeId, nodes, devices);
      return computeStats(ids, selectedIds);
    },
    [nodes, devices, selectedIds]
  );

  const rootStats = useMemo<ScopeStats>(
    () => computeStats(devices.map((d) => d.id), selectedIds),
    [devices, selectedIds]
  );

  // Mutators
  const toggleDevice = useCallback((id: NodeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setManyDevices = useCallback((ids: NodeId[], selected: boolean) => {
    if (ids.length === 0) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const selectAllInScope = useCallback(
    (scopeNodeId: NodeId | null) => {
      const ids = descendantLeafDeviceIds(scopeNodeId, nodes, devices);
      setManyDevices(ids, true);
    },
    [nodes, devices, setManyDevices]
  );

  const clearScope = useCallback(
    (scopeNodeId: NodeId | null) => {
      const ids = descendantLeafDeviceIds(scopeNodeId, nodes, devices);
      setManyDevices(ids, false);
    },
    [nodes, devices, setManyDevices]
  );

  const clearAll = useCallback(() => setSelectedIds(new Set()), []);

  const focusNode = useCallback((id: NodeId | null) => {
    setFocusedNodeId(id);
  }, []);

  const handleSelectAllVisible = useCallback(
    (selectAll: boolean) => {
      const ids = visibleDevices.map((d) => d.id);
      setManyDevices(ids, selectAll);
    },
    [visibleDevices, setManyDevices]
  );

  // Total in current scope (unfiltered by search) for the summary bar
  const scopeTotalDevices = useMemo(
    () => descendantLeafDeviceIds(focusedNodeId, nodes, devices).length,
    [focusedNodeId, nodes, devices]
  );
  const scopeSelectedCount = useMemo(() => {
    const ids = new Set(descendantLeafDeviceIds(focusedNodeId, nodes, devices));
    let c = 0;
    selectedIds.forEach((id) => {
      if (ids.has(id)) c += 1;
    });
    return c;
  }, [focusedNodeId, nodes, devices, selectedIds]);

  return (
    <div className={`device-selector ${className ?? ''}`}>
      <SummaryBar
        selectedCount={scopeSelectedCount}
        totalCount={scopeTotalDevices}
        focusedNodeName={focusedNodeName}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onClearAll={clearAll}
        canClear={selectedIds.size > 0}
      />
      <div className="device-selector__panes">
        <div className="device-selector__left-pane">
          <LocationTree
            nodes={nodes}
            rootIds={rootIds}
            focusedNodeId={focusedNodeId}
            rootStats={rootStats}
            computeStats={computeNodeStats}
            onFocus={focusNode}
            onSelectAllInScope={selectAllInScope}
            onClearScope={clearScope}
          />
        </div>
        <div className="device-selector__right-pane">
          <DeviceList
            focusedNodeName={focusedNodeName}
            visibleDevices={visibleDevices}
            selectedIds={selectedIds}
            parentLabNameById={labNameById}
            searchActive={searchQuery.trim().length > 0}
            onToggle={toggleDevice}
            onSelectAllVisible={handleSelectAllVisible}
            renderMeta={renderDeviceMeta as ((d: DeviceItem) => React.ReactNode) | undefined}
          />
        </div>
      </div>
    </div>
  );
};
